import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  ApiCollectionResponse,
  ApiSingleResponse,
  ApiSimpleResponse,
} from '../interfaces/responses/api-response';
import { RegistrationStatus } from '../interfaces/registration';
import {
  AdminRegistrationDetail,
  AdminRegistrationListItem,
  RegistrationCounts,
} from '../interfaces/registration-admin';

/** File de validation des inscriptions en ligne (rôle superviseur). */
@Injectable({ providedIn: 'root' })
export class RegistrationAdminService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.BASE_URL}/admin/registrations`;

  search(
    status?: RegistrationStatus | null,
    search?: string | null,
    page = 1,
    pageSize = 25,
  ): Observable<ApiCollectionResponse<AdminRegistrationListItem>> {
    let params = new HttpParams()
      .set('page', page)
      .set('pageSize', pageSize);

    if (status) params = params.set('status', status);
    if (search?.trim()) params = params.set('search', search.trim());

    return this.http.get<ApiCollectionResponse<AdminRegistrationListItem>>(
      this.baseUrl,
      { params },
    );
  }

  /**
   * Nombre de demandes par statut.
   *
   * <p>Le terme de recherche est transmis : sans lui, les facettes
   * annonceraient des totaux sans rapport avec ce que l'écran affiche dès
   * qu'une recherche est active.</p>
   */
  getCounts(search?: string | null): Observable<ApiSingleResponse<RegistrationCounts>> {
    let params = new HttpParams();
    if (search?.trim()) params = params.set('search', search.trim());

    return this.http.get<ApiSingleResponse<RegistrationCounts>>(
      `${this.baseUrl}/counts`,
      { params },
    );
  }

  getById(id: number): Observable<ApiSingleResponse<AdminRegistrationDetail>> {
    return this.http.get<ApiSingleResponse<AdminRegistrationDetail>>(
      `${this.baseUrl}/${id}`,
    );
  }

  approve(id: number): Observable<ApiSingleResponse<AdminRegistrationDetail>> {
    return this.http.post<ApiSingleResponse<AdminRegistrationDetail>>(
      `${this.baseUrl}/${id}/approve`,
      {},
    );
  }

  reject(
    id: number,
    reason: string,
  ): Observable<ApiSingleResponse<AdminRegistrationDetail>> {
    return this.http.post<ApiSingleResponse<AdminRegistrationDetail>>(
      `${this.baseUrl}/${id}/reject`,
      { reason },
    );
  }

  /**
   * Ouvre l'accès sans justificatif de paiement (bourse, geste commercial,
   * règlement hors ligne). Le motif est obligatoire et reste au journal.
   */
  waiveFee(
    id: number,
    reason: string,
  ): Observable<ApiSingleResponse<AdminRegistrationDetail>> {
    return this.http.post<ApiSingleResponse<AdminRegistrationDetail>>(
      `${this.baseUrl}/${id}/waive-fee`,
      { reason },
    );
  }

  resendActivation(id: number): Observable<ApiSimpleResponse> {
    return this.http.post<ApiSimpleResponse>(
      `${this.baseUrl}/${id}/resend-activation`,
      {},
    );
  }
}
